/**
 * SessionPage — one conversation (`/products/:pid/chats/:sid`).
 *
 * Two panes: whatever the guide last drew, beside the transcript. The old
 * four-mode map, its camera, and its stepper are gone (DECISIONS #62) — the
 * visual half is the diagram pane now, and it only ever shows something the
 * conversation asked for.
 *
 * The layout affordances survive the map unchanged, because they were never
 * about the map:
 *  - Collapse/expand: a chevron tab in the boundary rail; `\` toggles.
 *    Persisted per product.
 *  - Width: 320 up to min(1000px, 65% of the window) via the 6px grip on the
 *    transcript's inner edge; double-click resets to 460; below ~380px the
 *    transcript flips to the compact type scale. Re-clamped at render so
 *    window resizes hold.
 *  - Side: transcript right by default, flipped from the layout menu.
 *
 * Round 7, owner: "The chat section collapse button sometimes overlaps with
 * map components." It did: the tab was absolutely placed on the pane's inner
 * edge, floating ON the drawing, and in a real session it sat over a node. So
 * the canvas area is now two boxes rather than one, and the tab lives in the
 * second: a RAIL, a narrow strip of pane chrome between the drawing and the
 * transcript, reserved in the layout.
 *
 * That is the fix rather than "show it on hover" because it removes the
 * problem instead of hiding it. The diagram measures its fit against the box
 * it is given (DiagramView), and the rail is not part of that box, so no
 * drawing at any zoom or pan can reach under the tab: the frame simply ends
 * where the rail begins. A hover-only control would still be over a node the
 * moment the pointer arrived, and it would cost the affordance its
 * discoverability, which the owner also asked to keep.
 *
 * The rail carries the pane's OTHER edge control too: the shapes key
 * (ShapeLegend), for the same reason.
 *
 * ONE exception, measured rather than assumed: below RAIL_RESERVE_MIN the
 * pane is not wide enough to give a gutter away. (A 820px window with the
 * sidebar out and a 460px transcript leaves the pane 104px, and the fit
 * bottoms out at its floor there: taking 24 more would push a whole drawing
 * out through both edges.) At that size the pane is a thumbnail, so the rail
 * floats over it instead of narrowing it. A tab over a thumbnail is a smaller
 * loss than a drawing cut off on both sides, and every real layout is far
 * above the threshold.
 *
 * The resize grip is the one boundary control that stays where it was: it is
 * absolutely placed inside the TRANSCRIPT column, over its own 16px of left
 * padding, so it has never had any diagram to cover.
 *
 * Route mount = "this session is on screen": the sid becomes the active
 * session and its sidebar dot clears. Unmounting clears only the viewing
 * register — an in-flight stream carries on.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { useProduct } from '../../api/queries';
import type { Ulid } from '../../api/types';
import { useRouteScope } from '../../lib/routeScope';
import { useChatStore } from '../../stores/chatStore';
import {
  clampTranscriptWidth,
  TRANSCRIPT_COMPACT_WIDTH,
  TRANSCRIPT_DEFAULT_WIDTH,
  useProjectUi,
  useUiStore,
} from '../../stores/uiStore';
import { useSessionActivity } from '../../shell/sessionActivity';
import ChatPanel from '../chat/ChatPanel';
import { attachTurn, isTurnActiveFor } from '../chat/streamController';
import { DiagramPane } from '../diagram/DiagramPane';
import { LayoutMenu } from './LayoutMenu';
import { ShapeLegend } from './ShapeLegend';

/**
 * The transcript's inner grip. The drag delta sign follows the side: on the
 * RIGHT the rail sits on the column's LEFT edge, so dragging left grows it.
 * While a drag runs the width is the page's local state (`onDrag`); the
 * persisted store gets one write when it ends, not one per pointer move
 * (usePanelResize does the same for the docks).
 */
export function TranscriptResizeHandle({
  productId,
  onDrag,
}: {
  productId: Ulid;
  /** the width while dragging, null when the drag ends */
  onDrag: (width: number | null) => void;
}) {
  const side = useProjectUi(productId).side;
  const setProjectUi = useUiStore((s) => s.setProjectUi);
  const drag = useRef<{ startX: number; startWidth: number; latest: number } | null>(null);

  const end = () => {
    if (drag.current === null) return;
    setProjectUi(productId, { transcriptWidth: drag.current.latest });
    drag.current = null;
    onDrag(null);
  };

  return (
    <div
      data-testid="transcript-resize"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize transcript"
      onPointerDown={(e) => {
        const startWidth = useUiStore.getState().projectUi(productId).transcriptWidth;
        drag.current = { startX: e.clientX, startWidth, latest: startWidth };
        if (typeof e.currentTarget.setPointerCapture === 'function') {
          e.currentTarget.setPointerCapture(e.pointerId);
        }
      }}
      onPointerMove={(e) => {
        if (drag.current === null) return;
        const dx = e.clientX - drag.current.startX;
        const raw = side === 'right' ? drag.current.startWidth - dx : drag.current.startWidth + dx;
        drag.current.latest = clampTranscriptWidth(raw);
        onDrag(drag.current.latest);
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => {
        setProjectUi(productId, { transcriptWidth: TRANSCRIPT_DEFAULT_WIDTH });
      }}
      className={`absolute top-0 z-20 flex h-full w-1.5 cursor-col-resize items-center justify-center bg-transparent transition-colors duration-(--motion-hover) hover:bg-(--border-strong) active:bg-(--accent) ${
        side === 'right' ? 'left-0' : 'right-0'
      }`}
    >
      <span aria-hidden className="flex flex-col gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} className="block h-0.5 w-0.5 rounded-pill bg-(--border-strong)" />
        ))}
      </span>
    </div>
  );
}

function CollapseTab({ productId }: { productId: Ulid }) {
  const { side, collapsed } = useProjectUi(productId);
  const setProjectUi = useUiStore((s) => s.setProjectUi);
  const chevron = (collapsed ? side === 'right' : side === 'left') ? '‹' : '›';

  return (
    <button
      type="button"
      data-testid="collapse-tab"
      title={collapsed ? 'Expand the transcript (\\)' : 'Collapse the transcript (\\)'}
      aria-label={collapsed ? 'Expand the transcript' : 'Collapse the transcript'}
      onClick={() => setProjectUi(productId, { collapsed: !collapsed })}
      /* In normal flow inside the rail: no absolute placement, so there is no
         way for it to land on top of anything. */
      className="flex h-14 w-4 shrink-0 cursor-pointer items-center justify-center rounded-pill border border-default bg-raised text-tertiary shadow-raised transition-colors duration-(--motion-hover) hover:text-primary"
    >
      <span aria-hidden>{chevron}</span>
    </button>
  );
}

/**
 * How wide the canvas must be before 24px of it is a gutter rather than a
 * quarter of the picture. Above this the rail is reserved layout; below it
 * the pane is a thumbnail and the rail floats (see the module note).
 */
export const RAIL_RESERVE_MIN = 240;

/** The element's live width, or null where nothing can measure (jsdom). */
function useElementWidth(ref: RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el === null || typeof ResizeObserver === 'undefined') return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/**
 * The boundary rail: the strip of pane chrome between the drawing and the
 * transcript. It is 24px, on the side the transcript is (or was, while
 * collapsed), and it holds the two controls that belong to the pane's edge
 * rather than to the drawing: the shapes key and the collapse tab.
 *
 * Both stay visible at rest, which is the point: they are how a reader learns
 * the picture's vocabulary and how they get the transcript back. Reserved,
 * neither can cover a node, because the drawing is fitted to the box beside
 * this one. Floating (a pane under RAIL_RESERVE_MIN), it carries no
 * background: the two controls have their own, and a 24px stripe of canvas
 * painted over a drawing would hide more than the buttons do.
 */
function BoundaryRail({ productId, floating }: { productId: Ulid; floating: boolean }) {
  const side = useProjectUi(productId).side;
  return (
    <div
      data-testid="pane-rail"
      data-side={side}
      data-floating={floating ? 'true' : 'false'}
      className={`z-30 flex w-6 flex-col items-center justify-center gap-2 ${
        floating
          ? `absolute top-0 bottom-0 ${side === 'right' ? 'right-0' : 'left-0'}`
          : 'relative shrink-0 bg-canvas'
      }`}
    >
      <ShapeLegend side={side} />
      <CollapseTab productId={productId} />
    </div>
  );
}

export function SessionPage() {
  const { pid = '', sid = '' } = useParams<'pid' | 'sid'>();
  const product = useProduct(pid.length > 0 ? pid : undefined);
  const projectUi = useProjectUi(pid);
  // Measured, not assumed: the pane's width is the window minus a sidebar
  // that folds and a transcript the reader drags, and the rail's answer
  // depends on the result rather than on any one of those.
  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasWidth = useElementWidth(canvasRef);
  const railFloats = canvasWidth !== null && canvasWidth < RAIL_RESERVE_MIN;
  // The transcript's width while its grip is being dragged; the store's
  // number otherwise (TranscriptResizeHandle writes it once, at the end).
  const [dragWidth, setDragWidth] = useState<number | null>(null);

  // Commit the route's session as the active one. SessionView's replay
  // effect waits on this.
  useEffect(() => {
    if (sid.length === 0) return;
    if (useChatStore.getState().activeSessionId !== sid) {
      useChatStore.getState().setActiveSession(sid);
    }
  }, [sid]);

  // A turn may be running on the server for this session with nobody here
  // pumping it (the page was reloaded mid-answer, or the session was opened
  // from the sidebar's "writing" row): attach and follow it. Idle sessions
  // answer 204 and nothing happens (2026-09-07).
  const qc = useQueryClient();
  useEffect(() => {
    if (sid.length === 0) return;
    if (isTurnActiveFor(sid)) return;
    void attachTurn(qc, sid);
  }, [qc, sid]);

  // The chat header's provenance line is repo-scoped; sessions are not. Point
  // the scope at the product's first repo (remembered one when it still
  // belongs here) so the header can report freshness without a :rid route.
  const repos = product.data?.repos;
  useEffect(() => {
    if (repos === undefined) return;
    const remembered = useUiStore.getState().activeProjectId;
    const resolved =
      remembered !== null && repos.some((r) => r.id === remembered)
        ? remembered
        : (repos[0]?.id ?? null);
    useRouteScope.getState().setRepoScope(resolved);
    useUiStore.getState().setActiveProject(resolved);
  }, [repos]);

  useEffect(() => {
    return () => {
      useRouteScope.getState().setRepoScope(null);
    };
  }, []);

  // Activity: mounted = seen; a reply that finishes in a hidden tab keeps its
  // dot until the tab is looked at again.
  useEffect(() => {
    if (sid.length === 0) return;
    const activity = useSessionActivity.getState();
    if (pid.length > 0) activity.recordProduct(sid, pid);
    activity.setViewing(sid);
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') {
        useSessionActivity.getState().markSeen(sid);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (useSessionActivity.getState().viewingSessionId === sid) {
        useSessionActivity.getState().setViewing(null);
      }
    };
  }, [sid, pid]);

  // `\` toggles collapse, everywhere except while typing.
  useEffect(() => {
    if (pid.length === 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '\\' || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target !== null &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      const ui = useUiStore.getState().projectUi(pid);
      useUiStore.getState().setProjectUi(pid, { collapsed: !ui.collapsed });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pid]);

  if (sid.length === 0) return null;

  const width = clampTranscriptWidth(dragWidth ?? projectUi.transcriptWidth);
  const compact = width < TRANSCRIPT_COMPACT_WIDTH;

  return (
    <div className="flex h-full flex-col">
      <div className={`flex min-h-0 flex-1 ${projectUi.side === 'left' ? 'flex-row-reverse' : ''}`}>
        {/* Drawing, then rail. The rail sits on the transcript's side, so it
            follows the same row reversal the panes do. */}
        <div
          ref={canvasRef}
          data-testid="canvas-area"
          data-rail={railFloats ? 'floating' : 'reserved'}
          className={`relative flex min-w-0 flex-1 bg-canvas ${
            projectUi.side === 'left' ? 'flex-row-reverse' : ''
          }`}
        >
          <div data-testid="diagram-area" className="relative h-full min-w-0 flex-1">
            <DiagramPane sessionId={sid} />
          </div>
          <BoundaryRail productId={pid} floating={railFloats} />
        </div>
        {!projectUi.collapsed ? (
          <div
            data-testid="transcript-column"
            data-side={projectUi.side}
            data-compact={compact ? 'true' : undefined}
            className={`relative min-w-0 shrink-0 overflow-y-auto bg-surface ${
              projectUi.side === 'right' ? 'border-l' : 'border-r'
            } border-default`}
            style={{
              width,
              ...(compact
                ? {
                    ['--text-transcript-size' as string]: '15px',
                    ['--text-transcript-line' as string]: '24px',
                  }
                : {}),
            }}
          >
            <div className="absolute top-2 right-2 z-20">
              <LayoutMenu productId={pid} />
            </div>
            <TranscriptResizeHandle productId={pid} onDrag={setDragWidth} />
            <ChatPanel sessionId={sid} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
